import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { ReportType, apiPostMedicalReportsMultifile } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { FileUploadDropZone } from "../../../components/ui-kit/file-upload-drop-zone";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { reportTypes } from "./enums";

type ReportFields = {
  report_date: string | null;
  report_type: ReportType | null;
  comment: string;
};

export const AddReportDialog = observer(function AddReportDialog({
  app,
  onClose,
  onSaved,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const patient = app.requirePatient();
  const api = app.seerlinqApi;

  const [fields] = useState<ReportFields>(() =>
    observable({
      report_date: null,
      report_type: null,
      comment: "",
    }),
  );
  const [files, setFiles] = useState<File[]>([]);

  const hasValidFields =
    !!fields.report_date && fields.report_type != null && files.length > 0;

  async function save() {
    if (!fields.report_date || fields.report_type == null || files.length === 0)
      return;
    try {
      const body = new FormData();
      files.forEach((file) => body.append("files", file));
      const response = await apiPostMedicalReportsMultifile({
        // The generated Body type declares `files: string[]`, but the SDK's
        // formDataBodySerializer expects the raw FormData (Blob/File) at runtime.
        body: body as unknown as { files: Array<string> },
        bodySerializer: (body) => body,
        query: {
          patient_id: patient.patientId,
          report_date: fields.report_date,
          report_type: fields.report_type,
          comment: fields.comment || null,
        },
      });

      if (response.data != null) {
        api.clearResponseCache();
        onSaved();
        onClose();
      }
    } catch (error) {
      window.handleError(error);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      className="max-h-[90dvh]"
    >
      <h3 className="mb-6 text-base font-bold">
        <Trans>Add report</Trans>
      </h3>

      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <div className="min-h-0 overflow-y-auto overflow-x-hidden py-1 px-1">
          <FormTable dataAdd>
            <FormRow
              label={t`Date`}
              data={
                <DateInput
                  model={fields}
                  attr="report_date"
                  type="date"
                  required
                />
              }
              mandatory
            />
            <FormRow
              label={t`Type`}
              data={
                <Select
                  model={fields}
                  attr="report_type"
                  values={reportTypes}
                  required
                />
              }
              mandatory
            />
            <FormRow
              label={t`Comment`}
              data={
                <TextInput model={fields} attr="comment" multiline="autosize" />
              }
            />
            <FormRow
              label={t`Files`}
              data={
                <FileUploadDropZone
                  multiple
                  onSelect={setFiles}
                  accept={[".pdf", ".png", ".jpg", ".jpeg", ".rtf", ".txt"]}
                />
              }
              mandatory
            />
          </FormTable>
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit" disabled={!hasValidFields}>
            <Trans>Add</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </Dialog>
  );
});
